import { useEffect, useState } from 'react'
import styled from 'styled-components'
import DrinkSVG from '../../../assets/chart/drink.svg?react'
import AlreadyDrinkSVG from '../../../assets/chart/already-drink.svg?react'
import {
  AlertStatusCard,
  ChartWithZoomable,
  OptimizedChartWrapper,
  RecommendationAction,
  RecommendationCard,
} from '../../../components'
import useViewport from '../../../hooks/useViewport'
import { getDDMM, getHHmm } from '../../../utils/dateConversion'
import { caffeineRecommendationPeriodTime } from '../../../utils/caffeineRecommendationPeriodTime'
import { groupByCaffeineHour } from '../../../utils/groupByCaffeineHour'
import { useChartZoomable, useRecommendation } from '../../../hooks'
import { useTranslation } from 'react-i18next'

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px;
`

const RecommendationContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-start;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid #d6d7db;
  border-radius: 8px;
`

const RecommendationCardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 16px;
  gap: 8px;
`

const InfoActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

function next24Hours({ dateNow }) {
  const dayInMs = 1000 * 60 * 60 * 24

  return dateNow + dayInMs
}

const OptimizedDashboard = ({
  predictionResult,
  optimizeResult,
  userShifts,
  sleepTime,
  recommendations,
  ingestedCaffeine,
  bacLevels,
}) => {
  const [graphSize, setGraphSize] = useState(720)
  const [seeTotalPeriod, setSeeTotalPeriod] = useState(false)
  const { width } = useViewport()
  const { getEightHoursBackAndTwentyHoursFowardData } = useChartZoomable({
    seeTotalPeriod,
  })
  const { loading, STATUS, updateRecommendation } = useRecommendation()
  const { t } = useTranslation('common')

  useEffect(() => {
    if (width <= 768) {
      setGraphSize(700)
      return
    }

    if (width > 768 && width <= 850) {
      setGraphSize(720)
      return
    }

    if (width >= 850 && width <= 1440) {
      setGraphSize(850)
      return
    }

    if (width > 1440) {
      setGraphSize(1024)
      return
    }

    setGraphSize(920)
    return
  }, [width])

  const getRecommendations = () => {
    const halfHour = 1000 * 60 * 30
    const dateNow = Date.now()
    const todayRecommendations = groupByCaffeineHour(recommendations).map(
      ({ caffeineHour, items }) => {
        const activeRecommendations = items.filter(
          recommendation => recommendation.status !== STATUS.COMPLETED,
        )

        if (
          caffeineHour >= dateNow - caffeineRecommendationPeriodTime &&
          caffeineHour <= next24Hours({ dateNow }) &&
          activeRecommendations.length > 0
        ) {
          const caffeineHourDate = new Date(caffeineHour)
          const caffeineProducts = Object.entries(
            activeRecommendations.reduce((products, recommendation) => {
              const product = recommendation.caffeineProduct
              products[product] = (products[product] ?? 0) + 1

              return products
            }, {}),
          )
            .map(([product, quantity]) => `${quantity}x ${product}`)
            .join(', ')
          const status = activeRecommendations.every(
            recommendation => recommendation.status === STATUS.ACKNOWLEDGED,
          )
            ? STATUS.ACKNOWLEDGED
            : STATUS.PENDING

          return (
            <RecommendationCard
              key={caffeineHour}
              icon={<DrinkSVG />}
              reccomendation={`${caffeineProducts} ${t('user_dashboard.optimized.at')} ${getHHmm({ hours: caffeineHourDate.getHours(), minutes: caffeineHourDate.getMinutes() })} ${t('user_dashboard.optimized.from')} ${getDDMM({ date: caffeineHourDate })}`}
              action={
                <RecommendationAction
                  id={activeRecommendations.map(
                    recommendation => recommendation.id,
                  )}
                  onClick={updateRecommendation}
                  status={status}
                  disabledAcknowledge={
                    loading ||
                    caffeineHourDate.getTime() > Date.now() + halfHour
                  }
                />
              }
            />
          )
        }
        return null
      },
    )

    const filteredRecommendations = todayRecommendations.filter(
      recommendation => {
        if (recommendation) return recommendation
      },
    )

    return filteredRecommendations
  }

  const haveCaffeineLastEightHours = groupByCaffeineHour(
    ingestedCaffeine,
  ).filter(intake => {
    return Date.now() - intake?.caffeineHour <= 1000 * 60 * 60 * 8
  })

  const getCaffeineIntake = () => {
    return haveCaffeineLastEightHours.map(({ caffeineHour, items }) => {
      const caffeineTaken = new Date(caffeineHour)
      const caffeineProducts = Object.entries(
        items
          .flatMap(item => item.caffeineProducts ?? [])
          .reduce((products, product) => {
            products[product] = (products[product] ?? 0) + 1

            return products
          }, {}),
      )
        .map(([product, quantity]) => `${quantity}x ${product}`)
        .join(', ')
      const caffeineDescription = caffeineProducts || `${items.length}x`

      return (
        <div
          style={{ display: 'flex', gap: 8 }}
          key={caffeineHour}
        >
          <RecommendationCard
            icon={<AlreadyDrinkSVG />}
            reccomendation={`${caffeineDescription} ${t('user_dashboard.optimized.at')} ${getHHmm(
              {
                hours: caffeineTaken.getHours(),
                minutes: caffeineTaken.getMinutes(),
              },
            )} ${t('user_dashboard.optimized.from')} ${getDDMM({ date: caffeineTaken })}`}
          />
        </div>
      )
    })
  }

  const dateNow = new Date().getTime()
  const predictionIndexNow = predictionResult.findIndex(
    item => item.timestamp > dateNow,
  )

  return (
    <Container>
      <AlertStatusCard
        alertStatus={predictionResult[predictionIndexNow || 0]?.prediction}
      />

      <ChartWithZoomable
        seeTotalPeriod={seeTotalPeriod}
        onChange={() => setSeeTotalPeriod(!seeTotalPeriod)}
      >
        <OptimizedChartWrapper
          predictionResult={getEightHoursBackAndTwentyHoursFowardData({
            action: predictionResult,
          })}
          optimizeResult={getEightHoursBackAndTwentyHoursFowardData({
            action: optimizeResult,
          })}
          userShifts={getEightHoursBackAndTwentyHoursFowardData({
            action: userShifts,
          })}
          sleepTime={getEightHoursBackAndTwentyHoursFowardData({
            action: sleepTime,
          })}
          recommendations={recommendations}
          graphSize={graphSize}
          ingestedCaffeine={getEightHoursBackAndTwentyHoursFowardData({
            action: ingestedCaffeine,
          })}
          bacLevels={bacLevels}
        />
      </ChartWithZoomable>

      <InfoActionContainer>
        {haveCaffeineLastEightHours?.length > 0 && (
          <RecommendationContainer>
            <span style={{ fontSize: 20, fontWeight: 600 }}>
              {t('user_dashboard.optimized.caffeine_ingested')}
            </span>
            <RecommendationCardContainer width={width}>
              {getCaffeineIntake()}
            </RecommendationCardContainer>
          </RecommendationContainer>
        )}

        {getRecommendations().length > 0 && (
          <RecommendationContainer>
            <span style={{ fontSize: 20, fontWeight: 600 }}>
              {t('user_dashboard.optimized.recommendations')}
            </span>
            <RecommendationCardContainer width={width}>
              {getRecommendations()}
            </RecommendationCardContainer>
          </RecommendationContainer>
        )}
      </InfoActionContainer>
    </Container>
  )
}

export default OptimizedDashboard
